import React from "react";

import InstantComponent from "components/dashboard/product-template-table/components/templates/instant/InstantComponent";
import MultiForeignKey from "components/dashboard/product-template-table/components/templates/multi-foreign-key/MultiForeignKey";
import MultiValue from "components/dashboard/product-template-table/components/templates/multi-value";
import {
  GetDataset,
  GetDatetime,
  GetDouble,
  GetFile,
  GetForeignKey,
  GetInstant,
  GetLong, GetMultiForeignKey,
  GetMultiValue,
  GetString,
  GetTime,
  GetTimespan,
  GetUsersignature
} from "components/dashboard/product-template-table/utils/defaultAttributes";
import { IDataCellProps, ITableDataEntryValue, MongoFile } from "components/dashboard/product-template-table/utils/interfaces";
import useUserHasRequiredRole from "hooks/useUserHasRequiredRole";
import { CellType } from "utils/enums";
import DateTime from "../../date-time/DateTime";
import Double from "../../double/Double";
import ForeignKey from "../../foreign-key/ForeignKey";
import Long from "../../long/Long";
import String from "../../string/String";
import Time from "../../time/Time";
import Timespan from "../../timespan";
import UserSignature from "../../user-signature/UserSignature";
import Dataset from "../../dataset/Dataset";
import EntryFile from "../../file/EntryFile";

export default function DataCell(props: IDataCellProps) {
    const userHasRequiredRole = useUserHasRequiredRole(props.editRequireRoles);

    let cellObj: ITableDataEntryValue = props.dataValue.entries[props.headerConfig.referenceName];
    const isCellEditable = !props.headerConfig?.locked && userHasRequiredRole;
    const edit = props.isInEditMode && isCellEditable;

    if (cellObj === undefined) {
      // Loads default values if not found in data, but found in config
      switch (props.headerConfig.type) {
        case CellType.TIME:
          cellObj = GetTime();
          break;
        case CellType.USER_SIGNATURE:
          cellObj = GetUsersignature();
          break;
        case CellType.DATETIME:
          cellObj = GetDatetime();
          break;
        case CellType.DOUBLE:
          cellObj = GetDouble();
          break;
        case CellType.LONG:
          cellObj = GetLong();
          break;
        case CellType.STRING:
          cellObj = GetString();
          break;
        case CellType.TIMESPAN:
          cellObj = GetTimespan();
          break;
        case CellType.FOREIGN_KEY:
          cellObj = GetForeignKey();
          break;
        case CellType.INSTANT:
          cellObj = GetInstant();
          break;
        case CellType.MULTI_VALUE:
          cellObj = GetMultiValue();
          break;
        case CellType.MULTI_FOREIGN_KEY:
          cellObj = GetMultiForeignKey();
          break;
        case CellType.DATASET:
          cellObj = GetDataset();
          break;
        case CellType.FILE:
          cellObj = GetFile();
          break;
        default:
          console.info(`Default cell of column type:${props.headerConfig.type} not implemented, returning undefined...`);
          return <Undefined />;
      }
    }

    // here only pass value to parent state that should be updated
    const handleParentStateChange = (receivedValue: any) => {
        //  TODO set default value if cell does not exists
        try {
            const exists = props.dataValue.entries[props.headerConfig.referenceName] !== undefined;
            if (exists) {
                props.changeHandler(props.indexEntry, props.headerConfig.referenceName, receivedValue);
            } else {
                cellObj.value = receivedValue;
                cellObj.referenceName = props.headerConfig.referenceName;
                props.changeHandler(props.indexEntry, props.headerConfig.referenceName, receivedValue, cellObj);
            }
        } catch (err) {
            console.log(`Error: ${err}, `)
        }
    }

    const toggleParentFunction = () => {
        try {
            const exists = props.dataValue.entries[props.headerConfig.referenceName] !== undefined;
            if (exists) {
                props.onClickSign(props.indexEntry, props.headerConfig.referenceName);
            } else {
                cellObj.referenceName = props.headerConfig.referenceName;
                props.onClickSign(props.indexEntry, props.headerConfig.referenceName, cellObj);
            }
        } catch (err) {
            console.log("Error while triggering parent sign column");
        }
    }

    switch (cellObj.type) {
        case CellType.LONG: {
            return <Long value={cellObj.value as number} selectionValues={props.headerConfig.selectionValues}
                unit={props.headerConfig.unit ? props.headerConfig.unit : ''}
                bgHighlightRanges={props.headerConfig.bgHighlightRanges}
                formatNumber={props.headerConfig.shortenText}
                isInEditMode={edit}
                changeHandler={handleParentStateChange}
            />
        }
        case CellType.DOUBLE: {
            return <Double value={cellObj.value as number} selectionValues={props.headerConfig.selectionValues}
                unit={props.headerConfig.unit ? props.headerConfig.unit : ''}
                isInEditMode={edit}
                formatNumber={props.headerConfig.shortenText}
                precision={props.headerConfig.decimalPlaces || props.headerConfig.decimalPlaces === 0 ? props.headerConfig.decimalPlaces : 2}
                bgHighlightRanges={props.headerConfig.bgHighlightRanges}
                changeHandler={handleParentStateChange}
            />
        }
        case CellType.STRING: { // selectone, visa kita neimplementuota dar
            const isShortenText = props.headerConfig.shortenText !== undefined ? props.headerConfig.shortenText : false;
            // @ts-ignore
            return <String value={cellObj.value}
                // @ts-ignore
                selectionValues={props.headerConfig.selectionValues}
                unit={props.headerConfig.unit ? props.headerConfig.unit : ''}
                changeHandler={handleParentStateChange}
                isInEditMode={edit}
                shortenText={isShortenText}
            />
        }
        case CellType.DATETIME: { // postinimas blogas(duomenu issaugojimas)
            // @ts-ignore
            return <DateTime value={cellObj.value} format={props.headerConfig.dateDisplayPattern} isInEditMode={edit}
                changeHandler={handleParentStateChange}
            />
        }
        case CellType.TIMESPAN: {
            // @ts-ignore
            return <Timespan timespan={cellObj.value} isInEditMode={edit} changeHandler={handleParentStateChange} />
        }
        case CellType.TIME: {
            // @ts-ignore
            return <Time value={cellObj.value} isInEditMode={edit} changeHandler={handleParentStateChange}
                unit={props.headerConfig.unit ? props.headerConfig.unit : ''} />
        }
        case CellType.USER_SIGNATURE: {
            // @ts-ignore
            return <UserSignature value={cellObj.value} isInEditMode={edit} changeHandler={toggleParentFunction} />
        }
        case CellType.FOREIGN_KEY: {
            // @ts-ignore
            return <ForeignKey value={cellObj.value}
                editable={edit}
                foreignTemplateKey={props.headerConfig.foreignTemplate || ""}
                changeHandler={handleParentStateChange}
                foreignKeyWrap={props.foreignKeyWrap}
            />
        }
        case CellType.INSTANT: // @ts-ignore
            return <InstantComponent value={cellObj.value} format={props.headerConfig.dateDisplayPattern} isInEditMode={edit} changeHandler={handleParentStateChange} />

        case CellType.MULTI_VALUE: // @ts-ignore
            return <MultiValue value={cellObj.value} isInEditMode={edit} changeHandler={handleParentStateChange} selectionValues={props.headerConfig.selectionValues} subType={props.headerConfig.subType} maxAge={props.maxAge} updateFrequency={props.updateFrequency} bgHighlightRanges={props.headerConfig.bgHighlightRanges} highlightColor={props.headerConfig.highlightColor} onHighlightColors={props.onHighlightColors} />

        case CellType.MULTI_FOREIGN_KEY: // @ts-ignore
            return <MultiForeignKey value={cellObj.value} editable={edit} foreignTemplateKey={props.headerConfig.foreignTemplate || ""} changeHandler={handleParentStateChange} foreignKeyWrap={props.foreignKeyWrap} />

        case CellType.DATASET:
            return <Dataset values={cellObj.value as string[] | null} changeHandler={handleParentStateChange} isInEditMode={edit} decimalPlaces={props.headerConfig.decimalPlaces} isSingleDataset bgHighlightRanges={props.headerConfig.bgHighlightRanges} highlightColor={props.headerConfig.highlightColor} maxAge={props.maxAge} updateFrequency={props.updateFrequency} onHighlightColors={props.onHighlightColors} />

        case CellType.FILE:
            return (
              <EntryFile
                entryData={props.dataValue}
                value={cellObj.value as MongoFile}
                cellId={props.headerConfig.referenceName}
                changeHandler={handleParentStateChange}
                isInEditMode={edit}
                showThumbnail={props.headerConfig.showThumbnail}
                fileType={props.headerConfig.fileType}
              />
            );

        default: {
            return <Undefined />
        }
    }
}

const Undefined = () => {
    return <div style={{
        textAlign: "center"
    }}>NULL</div>
}